import {TIPOS_DE_EMPRESA, type Empresa, type ResultadoDeEliminarEmpresas} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  useAliasDeEmpresa,
  useEliminarEmpresas,
  useEmpresas,
  useUnirEmpresas,
  useUnirVariasEmpresas,
} from '../api/empresas';
import {BotonDeEliminar, type RevisionDeEliminacion} from '../componentes/boton-de-eliminar';
import {AYUDA_EMPRESAS as A} from '../componentes/ayuda-general';
import {FichaDeEmpresa} from '../componentes/ficha-de-empresa';
import {nombreDeTipo} from '../componentes/tipos-de-empresa';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {Plantillas} from './documentos-admin';
import {tr, trx} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

const rol = nombreDeTipo;

// Empresas y unir duplicadas (decision 0028): cuando un archivo trae otro
// nombre para una empresa (p. ej. "CM" por Colombian Mint) la app la crea
// aparte; aqui se une con la correcta y el nombre queda como alias.
export function PaginaEmpresas() {
  const permisos = usePermisos();
  const empresas = useEmpresas();
  const unir = useUnirEmpresas();
  const unirVarias = useUnirVariasEmpresas();
  const [buscar, setBuscar] = useState('');
  const [destinos, setDestinos] = useState<Record<number, string>>({});
  // Unir varias de una vez (Daniel, 12/10/2026): se marcan y se unen con una.
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [destinoVarias, setDestinoVarias] = useState('');
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  // Eliminar (Daniel, 14/10/2026): lo que quedo sin empresa, para ajustarlo.
  const eliminar = useEliminarEmpresas();
  const [informe, setInforme] = useState<ResultadoDeEliminarEmpresas | null>(null);
  // Ficha abierta (Daniel, 15/10/2026): una empresa, una nueva o ninguna.
  const [ficha, setFicha] = useState<Empresa | 'nueva' | null>(null);
  const [tipo, setTipo] = useState('');

  const lista = empresas.data?.empresas ?? [];
  const patron = buscar.trim().toLowerCase();
  const visibles = lista.filter(
    e =>
      (!tipo || e.roles.includes(tipo)) &&
      (!patron ||
        e.nombre.toLowerCase().includes(patron) ||
        (e.abreviacion ?? '').toLowerCase().includes(patron) ||
        e.alias.some(a => a.toLowerCase().includes(patron))),
  );
  const todasMarcadas = visibles.length > 0 && visibles.every(e => marcadas.has(e.id));
  const marcar = (id: number, si: boolean) =>
    setMarcadas(m => {
      const n = new Set(m);
      if (si) n.add(id);
      else n.delete(id);
      return n;
    });
  const marcarVisibles = (si: boolean) =>
    setMarcadas(m => {
      const n = new Set(m);
      for (const e of visibles) {
        if (si) n.add(e.id);
        else n.delete(e.id);
      }
      return n;
    });

  const unirMarcadas = async () => {
    const destino = lista.find(e => String(e.id) === destinoVarias);
    const origenes = lista.filter(e => marcadas.has(e.id) && e.id !== destino?.id);
    if (!destino || !origenes.length) return;
    const exportaciones = origenes.reduce((s, e) => s + e.exportaciones, 0);
    if (
      !window.confirm(
        tr(
          `¿Unir ${origenes.length} empresas con "${destino.nombre}"? Sus ${exportaciones} exportaciones, sus Big Bags y todo lo demás pasan a "${destino.nombre}", y sus nombres quedan como otros nombres de esa empresa. Se puede deshacer desde la Bitácora.\n\n${origenes.map(e => e.nombre).join('\n')}`,
          `Merge ${origenes.length} companies into "${destino.nombre}"? Their ${exportaciones} exports, Big Bags and everything else move to "${destino.nombre}", and their names become other names of that company. It can be undone from the Audit log.\n\n${origenes.map(e => e.nombre).join('\n')}`,
        ),
      )
    )
      return;
    setMensaje(null);
    try {
      const r = await unirVarias.mutateAsync({origenes: origenes.map(e => e.id), destino: destino.id});
      const total = r.cambios.reduce((s, c) => s + c.registros, 0);
      setMarcadas(new Set());
      setDestinoVarias('');
      setMensaje({
        texto: tr(
          `Listo: ${r.unidas.length} empresas se unieron con "${r.destino}" (${numero(total)} registros cambiaron). Las próximas cargas que traigan esos nombres quedan en "${r.destino}".`,
          `Done: ${r.unidas.length} companies were merged into "${r.destino}" (${numero(total)} records changed). Future uploads with those names go to "${r.destino}".`,
        ),
        error: false,
      });
    } catch (error) {
      setMensaje({
        texto: error instanceof ErrorDeApi ? error.message : tr('No se pudo unir.', 'Could not merge.'),
        error: true,
      });
    }
  };

  const unirCon = async (origen: Empresa) => {
    const destino = lista.find(e => String(e.id) === destinos[origen.id]);
    if (!destino) return;
    if (
      !window.confirm(
        tr(
          `¿Unir "${origen.nombre}" con "${destino.nombre}"? Sus ${origen.exportaciones} exportaciones, sus Big Bags y todo lo demás pasan a "${destino.nombre}", y "${origen.nombre}" queda como otro nombre de esa empresa. Se puede deshacer desde la Bitácora.`,
          `Merge "${origen.nombre}" into "${destino.nombre}"? Its ${origen.exportaciones} exports, Big Bags and everything else move to "${destino.nombre}", and "${origen.nombre}" becomes another name of that company. It can be undone from the Audit log.`,
        ),
      )
    )
      return;
    setMensaje(null);
    try {
      const r = await unir.mutateAsync({origen: origen.id, destino: destino.id});
      const total = r.cambios.reduce((s, c) => s + c.registros, 0);
      setMensaje({
        texto: tr(
          `Listo: "${r.origen}" se unió con "${r.destino}" (${numero(total)} registros cambiaron). Las próximas cargas que digan "${r.origen}" quedan en "${r.destino}".`,
          `Done: "${r.origen}" was merged into "${r.destino}" (${numero(total)} records changed). Future uploads saying "${r.origen}" go to "${r.destino}".`,
        ),
        error: false,
      });
    } catch (error) {
      setMensaje({
        texto: error instanceof ErrorDeApi ? error.message : tr('No se pudo unir.', 'Could not merge.'),
        error: true,
      });
    }
  };

  return (
    <section className="max-w-6xl space-y-4">
      <Link to="/configuracion-de-campos" className="text-sm text-verde hover:underline">
        {tr('← Configuración de campos', '← Field settings')}
      </Link>
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Empresas', 'Companies')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Proveedores, exportadores, compradores, clientes, traders, importadores, navieras y demás empresas. Con "＋ Nueva empresa" la creas con su ficha y queda lista para usarla en exportaciones, pagos y demás; clic en el nombre abre su ficha. Si una empresa aparece dos veces (por ejemplo "CM" y "Colombian Mint"), únela con la correcta: todo pasa a esa, sus datos completan lo que le falte y el nombre repetido queda como otro nombre suyo, así las próximas cargas no la vuelven a crear.',
            'Suppliers, exporters, buyers, clients, traders, importers, shipping lines and other companies. With "＋ New company" you create it with its record and it is ready to use in exports, payments and the rest; clicking the name opens its record. If a company appears twice (for example "CM" and "Colombian Mint"), merge it into the right one: everything moves there, its data fills what is missing and the repeated name becomes another of its names, so future uploads do not create it again.',
          )}
        </p>
      </div>
      {ficha && (
        <FichaDeEmpresa
          key={ficha === 'nueva' ? 'nueva' : `${ficha.id}-${ficha.version}`}
          empresa={ficha === 'nueva' ? null : ficha}
          puedeEditar={permisos.editar}
          onCerrar={() => setFicha(null)}
          onGuardada={(e, creada) => {
            setFicha(null);
            setMensaje({
              texto: creada
                ? tr(`Listo: se creó "${e.nombre}".`, `Done: "${e.nombre}" was created.`)
                : tr(`Listo: se guardó la ficha de "${e.nombre}".`, `Done: the record of "${e.nombre}" was saved.`),
              error: false,
            });
          }}
        />
      )}
      {ficha && ficha !== 'nueva' && permisos.administrar && (
        <div className="rounded-lg border border-linea bg-white p-4">
          <Plantillas key={ficha.id} empresa={{id: ficha.id, nombre: ficha.abreviacion ?? ficha.nombre}} />
        </div>
      )}
      {permisos.administrar && <OtrosNombres />}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <input
            value={buscar}
            onChange={e => setBuscar(e.target.value)}
            placeholder={tr('Buscar empresa', 'Search company')}
            aria-label={tr('Buscar empresa', 'Search company')}
            className="w-72 rounded-md border border-linea px-3 py-1.5 text-sm"
          />
          <NotaDeAyuda titulo={tr('Buscar empresa', 'Search company')} texto={A.buscar} />
        </div>
        <div className="flex items-center gap-1">
          <select
            value={tipo}
            onChange={e => setTipo(e.target.value)}
            aria-label={tr('Tipo de empresa', 'Company type')}
            className="rounded-md border border-linea bg-white px-2 py-1.5 text-sm"
          >
            <option value="">{tr('Todos los tipos', 'All types')}</option>
            {TIPOS_DE_EMPRESA.map(t => (
              <option key={t} value={t}>
                {rol(t)}
              </option>
            ))}
          </select>
          <NotaDeAyuda titulo={tr('Tipo de empresa', 'Company type')} texto={A.filtroTipo} />
        </div>
        {permisos.editar && (
          <button
            type="button"
            onClick={() => setFicha('nueva')}
            className="ml-auto rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro"
          >
            {tr('＋ Nueva empresa', '＋ New company')}
          </button>
        )}
      </div>
      {permisos.administrar && (
        <p className="text-xs text-apagado">
          {tr(
            'Para unir muchas de una vez: busca el nombre, marca las casillas (la de arriba marca todas las que se ven) y elige con cuál se unen.',
            'To merge many at once: search the name, tick the boxes (the one on top ticks all visible) and choose which one they merge into.',
          )}
        </p>
      )}
      {permisos.administrar && marcadas.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-dorado bg-dorado-claro px-3 py-2 text-sm"
          data-prueba="unir-varias"
        >
          <span className="font-medium">
            {tr(`${marcadas.size} marcadas: unir con`, `${marcadas.size} ticked: merge into`)}
          </span>
          <select
            value={destinoVarias}
            onChange={ev => setDestinoVarias(ev.target.value)}
            aria-label={tr('Unir las marcadas con', 'Merge the ticked ones into')}
            className="max-w-64 rounded-md border border-linea bg-white px-2 py-1 text-sm"
          >
            <option value="">—</option>
            {lista.map(o => (
              <option key={o.id} value={o.id}>
                {o.nombre}
              </option>
            ))}
          </select>
          <NotaDeAyuda texto={A.unirMarcadas} />
          <button
            type="button"
            disabled={!destinoVarias || unirVarias.isPending}
            onClick={() => void unirMarcadas()}
            className="rounded-md bg-verde px-3 py-1 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {tr('Unir las marcadas', 'Merge the ticked ones')}
          </button>
          <button
            type="button"
            onClick={() => setMarcadas(new Set())}
            className="rounded-md border border-linea bg-white px-2 py-1 text-sm hover:bg-papel"
          >
            {tr('Quitar marcas', 'Clear ticks')}
          </button>
          <span className="ml-auto">
            <BotonDeEliminar
              etiqueta={tr(`🗑 Eliminar las ${marcadas.size} marcadas`, `🗑 Delete the ${marcadas.size} ticked`)}
              titulo={tr('Eliminar empresas', 'Delete companies')}
              siempreRevisar
              revisar={async () => lineasDeRevision(await eliminar.mutateAsync({ids: [...marcadas], modo: 'revisar'}))}
              eliminar={async () => {
                const r = await eliminar.mutateAsync({ids: [...marcadas], modo: 'eliminar'});
                setInforme(r);
                setMarcadas(new Set());
                return tr(`Se eliminaron: ${r.empresas.join(', ')}.`, `Deleted: ${r.empresas.join(', ')}.`);
              }}
            />
          </span>
          {destinoVarias && marcadas.has(Number(destinoVarias)) && (
            <span className="text-xs text-apagado">
              {tr(
                '(la que queda está marcada: se deja tal cual y se unen las demás)',
                '(the one that stays is ticked: it is kept and the rest are merged)',
              )}
            </span>
          )}
        </div>
      )}
      {mensaje && (
        <p role="status" className={`text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
          {mensaje.texto}
        </p>
      )}
      {informe && <ParaAjustar r={informe} onCerrar={() => setInforme(null)} />}
      {empresas.isError && <p className="text-sm text-alerta">{empresas.error.message}</p>}
      <div className="overflow-x-auto rounded-lg border border-linea bg-white">
        <table className="w-full text-sm">
          <thead className="bg-papel text-left text-xs text-apagado">
            <tr>
              {permisos.administrar && (
                <th className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={todasMarcadas}
                    onChange={ev => marcarVisibles(ev.target.checked)}
                    aria-label={tr('Marcar todas las que se ven', 'Tick all visible')}
                  />
                </th>
              )}
              <th className="px-3 py-2 font-medium">
                {tr('Empresa', 'Company')}
                <NotaDeAyuda texto={A.empresa} />
              </th>
              <th className="px-3 py-2 font-medium">
                {tr('Abreviación', 'Abbreviation')}
                <NotaDeAyuda texto={A.abreviacionTabla} />
              </th>
              <th className="px-3 py-2 font-medium">
                ID
                <NotaDeAyuda texto={A.identificacionTabla} />
              </th>
              <th className="px-3 py-2 font-medium">
                {tr('Tipo', 'Type')}
                <NotaDeAyuda texto={A.roles} />
              </th>
              <th className="px-3 py-2 font-medium">
                {tr('Otros nombres', 'Other names')}
                <NotaDeAyuda texto={A.otrosNombres} />
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {tr('Exportaciones', 'Exports')}
                <NotaDeAyuda texto={A.exportaciones} />
              </th>
              <th className="px-3 py-2 text-right font-medium">
                Big Bags
                <NotaDeAyuda texto={A.bigBags} />
              </th>
              {permisos.administrar && (
                <th className="px-3 py-2 font-medium">
                  {tr('Unir con', 'Merge into')}
                  <NotaDeAyuda texto={A.unirCon} />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibles.map(e => (
              <tr key={e.id} className="border-t border-linea/70">
                {permisos.administrar && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={marcadas.has(e.id)}
                      onChange={ev => marcar(e.id, ev.target.checked)}
                      aria-label={tr(`Marcar ${e.nombre}`, `Tick ${e.nombre}`)}
                    />
                  </td>
                )}
                <td className="px-3 py-2 font-medium">
                  <button
                    type="button"
                    onClick={() => setFicha(e)}
                    title={trx(A.ficha)}
                    className="text-left text-verde-oscuro hover:underline"
                  >
                    {e.nombre}
                  </button>
                  {!e.activa && <span className="ml-1 text-xs text-apagado">{tr('(inactiva)', '(inactive)')}</span>}
                </td>
                <td className="px-3 py-2">{e.abreviacion ?? '—'}</td>
                <td className="px-3 py-2 text-apagado">{e.identificacion ?? '—'}</td>
                <td className="px-3 py-2 text-apagado">{e.roles.map(rol).join(', ') || '—'}</td>
                <td className="px-3 py-2 text-apagado">{e.alias.join(', ') || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{numero(e.exportaciones)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{numero(e.bigBags)}</td>
                {permisos.administrar && (
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={destinos[e.id] ?? ''}
                        onChange={ev => setDestinos(d => ({...d, [e.id]: ev.target.value}))}
                        aria-label={tr(`Unir ${e.nombre} con`, `Merge ${e.nombre} into`)}
                        className="max-w-48 rounded-md border border-linea px-2 py-1 text-sm"
                      >
                        <option value="">—</option>
                        {lista
                          .filter(o => o.id !== e.id)
                          .map(o => (
                            <option key={o.id} value={o.id}>
                              {o.nombre}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        disabled={!destinos[e.id] || unir.isPending}
                        onClick={() => void unirCon(e)}
                        className="rounded-md border border-linea bg-white px-2 py-1 text-sm hover:bg-papel disabled:opacity-50"
                      >
                        {tr('Unir', 'Merge')}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Lo que dice el aviso antes de eliminar: si alguna no se puede, solo eso (y
// no se elimina nada); si no, que queda sin empresa.
const listaCorta = (codigos: string[]) => `${codigos.slice(0, 12).join(', ')}${codigos.length > 12 ? '…' : ''}`;
function lineasDeRevision(r: ResultadoDeEliminarEmpresas): RevisionDeEliminacion {
  if (r.bloqueadas.length)
    return {
      cantidad: 0,
      lineas: [
        tr('No se puede eliminar ninguna mientras:', 'None can be deleted while:'),
        ...r.bloqueadas.map(b => `${b.empresa}: ${b.motivo}`),
      ],
    };
  const lineas = [tr(`Se eliminan: ${r.empresas.join(', ')}.`, `To delete: ${r.empresas.join(', ')}.`)];
  if (r.exportaciones.length)
    lineas.push(
      tr(
        `Quedan sin empresa ${r.exportaciones.length} exportaciones: ${listaCorta(r.exportaciones.map(x => x.codigo))}.`,
        `${r.exportaciones.length} exports are left without a company: ${listaCorta(r.exportaciones.map(x => x.codigo))}.`,
      ),
    );
  if (r.financiaciones.length)
    lineas.push(
      tr(
        `${r.financiaciones.length} financiaciones la nombran y en Cashflow saldrán con el código, sin empresa: ${listaCorta(r.financiaciones.map(x => x.codigo))}.`,
        `${r.financiaciones.length} financings name it and will show the code, without a company, in Cashflow: ${listaCorta(r.financiaciones.map(x => x.codigo))}.`,
      ),
    );
  if (r.pagos)
    lineas.push(
      tr(
        `${numero(r.pagos)} pagos a terceros o acuerdos la nombran.`,
        `${numero(r.pagos)} third-party payments or agreements name it.`,
      ),
    );
  if (r.bigBags)
    lineas.push(
      tr(
        `${numero(r.bigBags)} Big Bags quedan sin exportador o proveedor.`,
        `${numero(r.bigBags)} Big Bags are left without exporter or supplier.`,
      ),
    );
  for (const o of r.otros) lineas.push(`${o.dato}: ${numero(o.registros)}`);
  if (lineas.length === 1) lineas.push(tr('Nada queda sin empresa.', 'Nothing is left without a company.'));
  else
    lineas.push(
      tr(
        'Si es la misma que otra, mejor únela: así nada queda sin empresa.',
        'If it is the same as another one, better merge it: then nothing is left without a company.',
      ),
    );
  return {cantidad: r.empresas.length, lineas};
}

// Despues de eliminar: lo que quedo sin empresa, con enlace para ajustarlo.
function ParaAjustar({r, onCerrar}: {r: ResultadoDeEliminarEmpresas; onCerrar: () => void}) {
  const nada = !r.exportaciones.length && !r.financiaciones.length && !r.pagos && !r.bigBags && !r.otros.length;
  const resto = [
    r.pagos
      ? tr(`Pagos a terceros o acuerdos: ${numero(r.pagos)}`, `Third-party payments or agreements: ${numero(r.pagos)}`)
      : null,
    r.bigBags ? tr(`Big Bags: ${numero(r.bigBags)}`, `Big Bags: ${numero(r.bigBags)}`) : null,
    ...r.otros.map(o => `${o.dato}: ${numero(o.registros)}`),
  ].filter(Boolean);
  const enlaces = (titulo: string, lista: ResultadoDeEliminarEmpresas['exportaciones'], ancla = '') =>
    lista.length > 0 && (
      <div>
        <div className="text-xs text-apagado">{titulo}</div>
        <ul className="flex flex-wrap gap-x-3 gap-y-1">
          {lista.map(x => (
            <li key={x.codigo}>
              <Link to={`/seguimiento/${encodeURIComponent(x.codigo)}${ancla}`} className="text-verde hover:underline">
                {x.codigo}
              </Link>{' '}
              <span className="text-xs text-apagado">({x.campos.join(', ')})</span>
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <section
      className="space-y-2 rounded-lg border border-dorado bg-dorado-claro px-3 py-2 text-sm"
      data-prueba="para-ajustar"
      aria-label={tr('Para ajustar', 'To fix')}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">
          {nada
            ? tr(
                `Se eliminaron ${r.empresas.join(', ')}. Nada quedó sin empresa.`,
                `${r.empresas.join(', ')} deleted. Nothing was left without a company.`,
              )
            : tr(
                `Se eliminaron ${r.empresas.join(', ')}. Esto quedó sin empresa y hay que ajustarlo:`,
                `${r.empresas.join(', ')} deleted. This was left without a company and needs fixing:`,
              )}
        </p>
        <button type="button" onClick={onCerrar} aria-label={tr('Cerrar', 'Close')} className="text-apagado">
          ×
        </button>
      </div>
      {enlaces(tr('Exportaciones', 'Exports'), r.exportaciones)}
      {enlaces(tr('Financiaciones', 'Financings'), r.financiaciones, '#financiacion')}
      {resto.length > 0 && <p className="text-xs">{resto.join(' · ')}</p>}
      {r.exportaciones.length > 0 && (
        <p className="text-xs text-apagado">
          {tr(
            'Las exportaciones sin exportador también salen en Calidad de datos hasta que se ajusten.',
            'Exports without an exporter also show in Data quality until they are fixed.',
          )}
        </p>
      )}
    </section>
  );
}

// Otros nombres de una empresa (Daniel, 13/10/2026): los codigos con que sale
// en los archivos (un codigo de tres letras por empresa). Asi
// Cashflow y las demas pantallas la muestran con su nombre. Si no existe, se crea.
function OtrosNombres() {
  const guardar = useAliasDeEmpresa();
  const [empresa, setEmpresa] = useState('');
  const [nombres, setNombres] = useState('');
  const lista = nombres
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);
  const r = guardar.data;
  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-lg border border-linea bg-white px-3 py-2 text-sm"
      aria-label={tr('Otros nombres de una empresa', 'Other names of a company')}
      data-prueba="otros-nombres"
      onSubmit={e => {
        e.preventDefault();
        if (empresa.trim() && lista.length)
          guardar.mutate({empresa: empresa.trim(), alias: lista}, {onSuccess: () => setNombres('')});
      }}
    >
      <label className="block text-xs text-apagado">
        {tr('Empresa', 'Company')}
        <NotaDeAyuda texto={A.empresaAlias} dentroDeEtiqueta />
        <input
          value={empresa}
          onChange={e => setEmpresa(e.target.value)}
          placeholder={tr('Nombre de la empresa', 'Company name')}
          className="mt-0.5 block w-56 rounded-md border border-linea px-2 py-1 text-sm text-tinta"
        />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Otros nombres o códigos (separados por coma)', 'Other names or codes (comma separated)')}
        <NotaDeAyuda texto={A.alias} dentroDeEtiqueta />
        <input
          value={nombres}
          onChange={e => setNombres(e.target.value)}
          placeholder="ABC, ABD"
          className="mt-0.5 block w-64 rounded-md border border-linea px-2 py-1 text-sm text-tinta"
        />
      </label>
      <button
        type="submit"
        disabled={!empresa.trim() || !lista.length || guardar.isPending}
        className="rounded-md bg-verde-oscuro px-3 py-1.5 text-white disabled:opacity-50"
      >
        {tr('Guardar', 'Save')}
      </button>
      <p className="w-full text-xs text-apagado">
        {guardar.isError
          ? guardar.error.message
          : r
            ? tr(
                `${r.empresa}${r.creada ? ' (nueva)' : ''}: ${r.aliasNuevos.length ? `ahora también es ${r.aliasNuevos.join(', ')}` : 'ya tenía esos nombres'}.`,
                `${r.empresa}${r.creada ? ' (new)' : ''}: ${r.aliasNuevos.length ? `now also ${r.aliasNuevos.join(', ')}` : 'already had those names'}.`,
              )
            : tr(
                'Escribe la empresa y los códigos con que sale en los archivos. Si la empresa no existe, se crea.',
                'Type the company and the codes it appears with in the files. If the company does not exist, it is created.',
              )}
      </p>
    </form>
  );
}
